import { Conviction, ConvictionType, CurrencyCode } from '@ndvr/portfolio-api';

import { AssetClass, AssetInfo, AssetType } from '@/cip/_openapi/portfolio-optimizer';
import { formatCurrency, formatNumber, formatPercent } from 'utils';

const EXCLUDED_BOND_ASSET_TYPES: AssetType[] = [AssetType.MBS, AssetType.AGENCY, AssetType.CERTIFICATE_OF_DEPOSIT];

/**
 * Mirrors the `excluded` branch of the dbt-side macro.
 * An excluded asset can't be part of the investment universe, so it should not be added as a conviction.
 */
export const isExcludedAsset = (asset: AssetInfo): boolean => {
    const { assetClass, assetType } = asset;

    if (assetClass === AssetClass.OTHER || assetClass === AssetClass.FUTURES || assetClass === AssetClass.OPTIONS) {
        return true;
    }
    if (assetClass === AssetClass.EQUITY && assetType === AssetType.OTHER) {
        return true;
    }
    if (assetClass === AssetClass.BOND && assetType !== undefined && EXCLUDED_BOND_ASSET_TYPES.includes(assetType)) {
        return true;
    }
    return false;
};

/**
 * An excluded conviction carries no target: the held position is labelled `excluded` by the optimizer (the same
 * label as assets excluded by asset class/type) and is neither traded nor targeted by any algo.
 */
export const isExcludedConviction = (convictionType?: ConvictionType): boolean =>
    convictionType === ConvictionType.EXCLUDED;

export const getConvictionTypeDisplay = (convictionType: ConvictionType) => {
    switch (convictionType) {
        case ConvictionType.EXPOSURE:
            return 'Target Exposure';
        case ConvictionType.QUANTITY:
            return 'Target Quantity';
        case ConvictionType.WEIGHT:
            return 'Target Weight';
        case ConvictionType.EXCLUDED:
            return 'Excluded Position';
        default:
            return convictionType;
    }
};

export const formatConvictionValue = (conviction: Conviction) => {
    switch (conviction.convictionType) {
        case ConvictionType.EXPOSURE:
            return formatCurrency(conviction.exposure, CurrencyCode.USD, 1);
        case ConvictionType.QUANTITY:
            return formatNumber(conviction.quantity || 0);
        case ConvictionType.WEIGHT:
            return formatPercent(parseFloat(conviction.weight || '0') * 100);
        case ConvictionType.EXCLUDED:
            return 'Not traded';
        default:
            return conviction.weight;
    }
};
